import L from 'leaflet'
import { polygon, featureCollection, intersect, union, area, booleanEqual } from '@turf/turf'
import GeoJsonAdapter from './geoJsonAdapter'
import { getPolygonDiff, getPolygonUnion, isPointInsideFeature } from './utils'

const DEFAULTS = {
  geoJsonLayer: {
    fill: false,
    color: '#FFFF00'
  }
}

export default class SustainablePracticesManager {
  _mapManager = null
  _map = null
  _drawItemsGroup = null
  _drawingControls = null
  _currentSP = null
  _center = null
  _isDrawingRestricted = false
  _propertyBounds = null
  _clickHandler = null

  constructor (mapManager) {
    this._mapManager = mapManager
    this._map = mapManager.map
    this._drawItemsGroup = mapManager.drawItemsGroup
    this._drawingControls = mapManager.drawingControls

    this._init()
  }

  _init () {
    this._addCustomControls(this)
    this._hideDrawingControls()
  }

  _addCustomControls (that) {
    const mapContainer = this._map.getContainer()
    if (mapContainer && mapContainer.querySelector('.sedap-base-map__zoom-custom-control')) {
      return
    }
    const CentralizationControl = L.Control.extend({
      options: {
        position: 'topright'
      },
      onAdd: function () {
        const zoomContainer = L.DomUtil.create('div', 'sedap-base-map__zoom-custom-control')
        const button = L.DomUtil.create('button', 'sedap-base-map__custom-control-btn', zoomContainer)

        button.innerHTML = '<i class="fas fa-crosshairs"></i>'
        button.title = 'Centralizar mapa'
        button.style.textAlign = 'center'
        button.style.cursor = 'pointer'

        L.DomEvent.on(button, 'click', (e) => {
          e.preventDefault()
          this._centralizeMap()
        })

        return zoomContainer
      },
      _centralizeMap: function () {
        if (that._center) {
          this._map.fitBounds(that._center)
          return
        }

        this._map.fitBounds(this._map.getBounds())
      }
    })

    this._map.addControl(new CentralizationControl())
  }

  _hideDrawingControls () {
    this._mapManager.toggleDrawing(true)

    const controls = document.querySelector('.leaflet-draw.leaflet-control')
    controls.style.transform = 'translateY(-50000px)'
  }

  _getPropertyBounds () {
    let property = null
    this._map.eachLayer((layer) => {
      if (layer.options.name === 'property') {
        property = layer.toGeoJSON()
      }
    })
    if (property && property.features && property.features.length > 0) {
      return property.features[0].geometry
    }
    return null
  }

  _isPointInsideProperty (latlng) {
    if (!this._propertyBounds) {
      this._propertyBounds = this._getPropertyBounds()
    }
    if (!this._propertyBounds) {
      return true
    }

    const propertyPolygon = polygon(this._propertyBounds.coordinates)
    return isPointInsideFeature(latlng, propertyPolygon)
  }

  _addDrawingRestrictions () {
    this._isDrawingRestricted = true
    this._propertyBounds = this._getPropertyBounds()
    this._clickHandler = (e) => {
      if (this._isDrawingRestricted) {
        const isInside = this._isPointInsideProperty(e.latlng)
        if (!isInside) {
          e.originalEvent.stopPropagation()
          e.originalEvent.preventDefault()
          this._mapManager._showAlert('Você só pode desenhar dentro dos limites da propriedade')
          return false
        }
      }
    }
    this._map.on('click', this._clickHandler)
    this._map.on('draw:drawstart', () => {
      this._isDrawingRestricted = true
    })
    this._map.on('draw:drawstop', () => {
      this._removeDrawingRestrictions()
    })
    this._map.on('draw:canceled', () => {
      this._removeDrawingRestrictions()
    })
  }

  _removeDrawingRestrictions () {
    this._isDrawingRestricted = false
    if (this._clickHandler) {
      this._map.off('click', this._clickHandler)
      this._clickHandler = null
    }
  }

  _drawWithinPropertyLimits (newLayer) {
    let property

    this._map.eachLayer((layer) => {
      if (layer.options.name === 'property') {
        property = layer.toGeoJSON().features
      }
    })

    newLayer = newLayer.toGeoJSON()

    if (newLayer.type === 'FeatureCollection') {
      newLayer = newLayer.features[0]
    }

    const coordLayer = polygon(newLayer.geometry.coordinates)

    const intersections = property[0].geometry.coordinates.map(coord => intersect(featureCollection([polygon([coord]), coordLayer])))
      .filter(Boolean)

    return intersections.length > 1 ? union(featureCollection(intersections)) : intersections[0]
  }

  _checkOverlappingAreas (currLayer) {
    let result = currLayer

    this._drawItemsGroup.eachLayer((layer) => {
      if (!layer.toGeoJSON || layer.options.name === 'property') return

      const layerGeoJSON = layer.toGeoJSON()
      let features = layerGeoJSON

      if (layerGeoJSON.type === 'FeatureCollection') {
        features = layerGeoJSON.features[0]
      }

      if (layer.options.style.color === currLayer.properties.color) {
        const union = getPolygonUnion(features, result)

        if (union) {
          this._drawItemsGroup.removeLayer(layer)
          return (result = union)
        }
      }

      const difference = getPolygonDiff(features, result)

      if (difference) {
        return (result = difference)
      }
    })
    return result
  }

  _setLayerProps () {
    return {
      style: {
        color: this._currentSP.legend,
        fillColor: this._currentSP.legend,
        fillOpacity: 0.2
      },
      name: this._currentSP.name
    }
  }

  _setLayerSavedProps (file) {
    return {
      style: {
        color: file.legend,
        fillColor: file.legend,
        fillOpacity: 0.2
      },
      name: file.name
    }
  }

  _applyDrawSavedToPropertyBoundaries (layer, file) {
    const copyLayer = layer

    this._drawItemsGroup.removeLayer(layer)

    let drawnArea = this._drawWithinPropertyLimits(copyLayer)

    if (!drawnArea) {
      return
    }

    drawnArea = this._checkOverlappingAreas(drawnArea)

    const newDrawnArea = L.geoJSON(drawnArea, { ...this._setLayerSavedProps(file) })

    const layers = newDrawnArea.getLayers()

    let totalArea = 0

    layers.forEach((layer) => {
      const geom = layer.feature.geometry

      if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach((coordinates) => {
          let polygon = { type: 'Polygon', coordinates }

          totalArea += area(polygon)

          L.geoJSON(polygon, {
            ...this._setLayerSavedProps(file),
            onEachFeature: (_, cLayer) => {
              this._drawItemsGroup.addLayer(cLayer)
            }
          })
        })
      } else {
        totalArea += area(geom)

        this._drawItemsGroup.addLayer(layer)
      }
    })
    this._setTotalDrawnAreaSaved(totalArea, file)

    return newDrawnArea
  }

  _applyDrawToPropertyBoundaries (layer) {
    const copyLayer = layer

    this._drawItemsGroup.removeLayer(layer)

    let drawnArea = this._drawWithinPropertyLimits(copyLayer)
    drawnArea.properties.color = this._currentSP.legend

    if (!drawnArea) {
      return
    }

    drawnArea = this._checkOverlappingAreas(drawnArea)

    const newDrawnArea = L.geoJSON(drawnArea, { ...this._setLayerProps() })

    const layers = newDrawnArea.getLayers()

    let totalArea = 0

    layers.forEach((layer) => {
      const geom = layer.feature.geometry

      if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach((coordinates) => {
          let polygon = { type: 'Polygon', coordinates }

          totalArea += area(polygon)

          L.geoJSON(polygon, {
            ...this._setLayerProps(),
            onEachFeature: (_, cLayer) => {
              this._drawItemsGroup.addLayer(cLayer)
            }
          })
        })
      } else {
        totalArea += area(geom)

        this._drawItemsGroup.addLayer(layer)
      }
    })

    this._setTotalDrawnArea(totalArea)

    return newDrawnArea
  }

  _setTotalDrawnAreaSaved (area, file) {
    this._drawItemsGroup.eachLayer((layer) => {
      if (layer.options.color === file.legend) {
        layer.options.vectorizedArea = {
          m2: area,
          ha: area / 10000,
          km2: area / 1000000
        }
      }
    })
  }

  _setTotalDrawnArea (area) {
    this._drawItemsGroup.eachLayer((layer) => {
      if (layer.options.color === this._currentSP.legend) {
        layer.options.vectorizedArea = {
          m2: area,
          ha: area / 10000,
          km2: area / 1000000
        }
      }
    })
  }

  _getTotalAreaSaved (file) {
    let totalArea

    this._drawItemsGroup.eachLayer((layer) => {
      if (layer.options.color === file.legend) {
        totalArea = layer.options.vectorizedArea
      }
    })

    return totalArea
  }

  _getTotalArea () {
    let totalArea

    this._drawItemsGroup.eachLayer((layer) => {
      if (layer.options.color === this._currentSP.legend) {
        totalArea = layer.options.vectorizedArea
      }
    })

    return totalArea
  }

  async _addShapeSaved (file) {
    let shape = file.geometry

    if (!shape.features) {
      const adapter = new GeoJsonAdapter(file)
      try {
        shape = await adapter.adapt()
      } catch (error) {
        throw new Error(`Erro ao adaptar camada para GeoJSON: ${error}`)
      }
    }

    const props = {
      style: {
        color: file.legend,
        fillColor: file.legend,
        fillOpacity: 0.2
      },
      name: file.name
    }

    let layer = L.geoJSON(shape, props)

    if (props.name !== 'property') {
      layer = this._applyDrawSavedToPropertyBoundaries(layer, file)
    }

    layer.addTo(this._drawItemsGroup)

    this._map.fitBounds(this._center)

    if (props.name !== 'property') {
      return this._getTotalAreaSaved(file)
    }

    return null
  }

  async _addShape (file) {
    let shape = file

    if (!shape.features) {
      const adapter = new GeoJsonAdapter(file)
      try {
        shape = await adapter.adapt()
      } catch (error) {
        throw new Error(`Erro ao adaptar camada para GeoJSON: ${error}`)
      }
    }

    let props

    if (!this._currentSP) {
      props = { name: 'property', style: DEFAULTS.geoJsonLayer }
    } else {
      props = {
        ...props,
        ...this._setLayerProps()
      }
    }

    let layer = L.geoJSON(shape, props)

    if (props.name !== 'property') {
      layer = this._applyDrawToPropertyBoundaries(layer)
    }

    layer.addTo(this._drawItemsGroup)

    this._center = layer.getBounds()
    this._map.fitBounds(this._center)

    if (props.name !== 'property') {
      return this._getTotalArea()
    }

    return null
  }

  get map () {
    return this._map
  }

  set currentSP (sp) {
    this._currentSP = sp
  }

  async importShape (file) {
    try {
      const area = await this._addShape(file)
      return area
    } catch (error) {
      console.error(error)
    }
  }

  async importShapeSaved (file) {
    try {
      const area = await this._addShapeSaved(file)
      return area
    } catch (error) {
      console.error(error)
    }
  }

  handleDrawCreated (evt) {
    const { layer } = evt
    this._applyDrawToPropertyBoundaries(layer)

    return this._getTotalArea()
  }

  initVectorization (sp) {
    this._currentSP = sp
    this._drawingControls.setDrawingOptions({
      polygon: {
        showArea: true,
        showLength: true,
        precision: {
          km: 1,
          ha: 1,
          m: 0
        },
        shapeOptions: {
          color: this._currentSP.legend
        }
      }

    })

    // Ativar restrições de desenho
    this._addDrawingRestrictions()

    document.querySelector('.leaflet-draw-draw-polygon').click()
  }

  deleteVectorization (sp) {
    this._currentSP = null
    this._removeDrawingRestrictions()

    this._drawItemsGroup.eachLayer((layer) => {
      if (layer.options.color === sp.legend || layer.options.style.color === sp.legend) {
        this._drawItemsGroup.removeLayer(layer)
      }
    })
  }

  deleteVectorizationPolygon (polygon, sp) {
    this._removeDrawingRestrictions()

    let i = 0
    let hasOtherSpLayer = false

    this._drawItemsGroup.eachLayer((layer) => {
      if (booleanEqual(layer.toGeoJSON(), polygon)) {
        this._drawItemsGroup.removeLayer(layer)
      }
    })

    const layersValues = Object.values(this._drawItemsGroup._layers)

    while (!hasOtherSpLayer && i < layersValues.length) {
      if (layersValues[i].options.color === sp.legend) {
        hasOtherSpLayer = true
      }
      i++
    }

    if (!hasOtherSpLayer) {
      this.deleteVectorization(sp)
      return null
    }

    let totalArea = 0

    this._drawItemsGroup.eachLayer((layer) => {
      if (layer.options.color === sp.legend) {
        totalArea += area(layer.feature)
      }
    })

    this._setTotalDrawnArea(totalArea)

    return this._getTotalArea()
  }

  getCurrentSpLayers () {
    let spLayers = []

    this._drawItemsGroup.eachLayer((layer) => {
      if (layer.options.color === this._currentSP.legend) {
        spLayers.push(layer.toGeoJSON())
      }
    })

    return spLayers
  }
}



// WEBPACK FOOTER //
// ./src/components(new)/UI/Maps/core/sustainablePracticesManager.js